import get from 'lodash-es/get';
import isNil from 'lodash-es/isNil';
import memoize from '@stripcash/ui-kit/utils/memoize';
import { createElement } from 'react';

import TextSkeleton from 'common/components/TextSkeleton';

import { Translate, GetTranslation } from './types';
import parseMessage from './parseMessage';

const concat = (...args: (string | undefined)[]) =>
  args.filter(Boolean).join('.');

const createGetKey = memoize(
  (
    translations: object,
    locale: string,
    onRequest: (key: string) => void,
    setLocale: (locale: string) => void,
  ) => {
    const createTranslateWith =
      (getter: GetTranslation, prevBase?: string) =>
      (base?: string): Translate => {
        const translate: Translate = (key, options) => {
          const tKey = concat(base, key);

          onRequest(concat(prevBase, tKey));

          return getter(tKey, options);
        };

        translate.locale = locale;
        translate.setLocale = setLocale;
        translate.rebase = createTranslateWith(translate, base);

        return translate;
      };

    return createTranslateWith((key, options) => {
      const contextMessage = options?.context
        ? get(translations, `${key}_${options.context}`)
        : '';
      const message = contextMessage || get(translations, key);

      if (!message) {
        if (!isNil(options?.fallback)) {
          return options?.fallback;
        }

        const [lastSegment, prevSegment = 'no'] = key.split('.').reverse();

        if (options?.showSkeleton === false) {
          return '';
        }

        return createElement(TextSkeleton, {
          width: lastSegment.length * 8 + prevSegment.length * 5,
        });
      }

      return parseMessage(message, options?.elements);
    })();
  },
  { max: 1 },
);

export default createGetKey;
